// Ekran 03 — numune tipine göre değişen analiz formu.
//
// Üç tip, üç alan kümesi (K22):
//   lot       → KM · OKM · Kül · pH   + OKM+Kül=100±3 canlı rozeti
//   fermenter → pH · FOS/TAC · VFA · NH₄-N · KM · oKM  + canlı eşik rozetleri
//   ürün      → Nem · N · P₂O₅ · K₂O
//
// Zorunluluklar ve ±3 kuralı sunucuda da var (409). Buradaki kontroller
// kullanıcıyı boşuna gidiş dönüşten kurtarıyor; rozet ise referanstaki gibi
// kaydet butonunu KİLİTLEMEZ — yalnız uyarır.

import { useEffect, useMemo, useState } from 'react'
import { tr, trGiris, sayiyaCevir } from '../../lib/trFormat'

import { BANT_ETIKET, bant as bantBul } from './esikBandi'
import {
  KUL_TOLERANS,
  kulGecerliMi,
  kulSapmasi,
  type AnalizAlanlari,
  type AnalizDto,
  type EsikDto,
  type NumuneTipi,
} from './api'

/**
 * `yerTutucu` alan BAŞINA sabittir; zorunluluktan TÜRETİLMEZ — referans
 * beklenen ondalık hassasiyetini yer tutucuyla anlatıyor (ör. FOS/TAC `0,00`,
 * VFA/NH₄ tam sayı `0`) ve aynı değerleri hem panelde (ref:534-539 · 551-554)
 * hem sekmede (ref:598-603 · 606-609) kullanıyor.
 */
type Alan = {
  anahtar: keyof AnalizAlanlari
  etiket: string
  yerTutucu: string
  ipucu?: string
}

const ALANLAR: Record<NumuneTipi, Alan[]> = {
  lot: [
    { anahtar: 'km', etiket: 'KM (% YM)', yerTutucu: '0,0' },
    { anahtar: 'ph', etiket: 'pH', yerTutucu: '—' },
    { anahtar: 'okm', etiket: 'OKM (% KM)', yerTutucu: '0,0' },
    { anahtar: 'kul', etiket: 'Kül (% KM)', yerTutucu: '0,0' },
  ],
  fermenter: [
    { anahtar: 'ph', etiket: 'pH', yerTutucu: '0,0' },
    { anahtar: 'fosTac', etiket: 'FOS/TAC', yerTutucu: '0,00' },
    { anahtar: 'vfa', etiket: 'VFA (mg/L)', yerTutucu: '0' },
    { anahtar: 'nh4', etiket: 'NH₄-N (mg/L)', yerTutucu: '0' },
    { anahtar: 'km', etiket: 'KM (%)', yerTutucu: '0,0' },
    { anahtar: 'okm', etiket: 'oKM (% KM)', yerTutucu: '0,0' },
  ],
  urun: [
    { anahtar: 'nem', etiket: 'Nem (%)', yerTutucu: '—' },
    { anahtar: 'n', etiket: 'N (kg/t)', yerTutucu: '0,0' },
    { anahtar: 'p', etiket: 'P₂O₅ (kg/t)', yerTutucu: '0,0' },
    { anahtar: 'k', etiket: 'K₂O (kg/t)', yerTutucu: '0,0' },
  ],
}

/** Tipe göre zorunlu alanlar — sunucudaki karşılığıyla aynı. */
const ZORUNLU: Record<NumuneTipi, (keyof AnalizAlanlari)[]> = {
  lot: ['km', 'okm', 'kul'],
  fermenter: ['ph', 'fosTac'],
  urun: ['n'],
}

const ZORUNLU_MESAJ: Record<NumuneTipi, string> = {
  lot: 'KM, OKM ve Kül alanları zorunludur',
  fermenter: 'pH ve FOS/TAC zorunludur',
  urun: 'N (kg/t) zorunludur',
}

/** §6.3 bandı → rozet sınıfı. */
export const BANT_SINIFI: Record<string, string> = {
  saglikli: 'badge b-navy',
  izle: 'badge b-amber',
  kritik: 'badge b-red',
}

/**
 * Fermenterde değer girildikçe rozet basılan ölçümler (ref:763-769) —
 * referanstaki sıra: pH · FOS/TAC · VFA · NH₄. `gosterge` `ayar_esik`
 * satırının adıdır, eşik sayısı koda yazılmaz.
 */
const CANLI_ROZET: { anahtar: keyof AnalizAlanlari; etiket: string; gosterge: string }[] = [
  { anahtar: 'ph', etiket: 'pH', gosterge: 'ph' },
  { anahtar: 'fosTac', etiket: 'FOS/TAC', gosterge: 'fos_tac' },
  { anahtar: 'vfa', etiket: 'VFA', gosterge: 'vfa' },
  { anahtar: 'nh4', etiket: 'NH₄', gosterge: 'nh4' },
]

type FormAyari = {
  tip: NumuneTipi
  /** Yeni kayıtta "#N" etiketi. */
  siraNo: number
  /** Doluysa revizyon modundayız (K15) — gerekçe zorunlu. */
  duzenlenen?: AnalizDto | null
  bekliyor: boolean
  yazabilir: boolean
  /**
   * Panel başlığındaki ünite/ürün eki (ref:532 · 549). Lot dalında referans ek
   * KOYMUYOR, sekmede de üç tipte de eksiz — bu yüzden opsiyonel.
   */
  kaynakAd?: string | null
  /** Canlı eşik rozetleri için `ayar_esik` satırları (fermenter). */
  esikler?: EsikDto[]
  onKaydet: (alanlar: AnalizAlanlari, revizyonGerekce?: string) => void
}

/**
 * Form durumu + doğrulama, gövdeden AYRI döner.
 *
 * Neden: inceleme panelinde kaydet butonu referansta `.insp-f` altlığındadır
 * (ref:562-565) — `.insp-b` kaydırılırken altlık sabit kalsın diye. Sekmede ise
 * buton kartın içindedir (ref:611-615). Aynı mantığı iki yerleşimde
 * kullanabilmek için alanlar `govde`, kaydetme `kaydet()` olarak ayrıldı.
 *
 * `govde` `.insp-sec` SARMALINI İÇERMEZ; sarmalı çağıran koyar, çünkü sekmede
 * butonlar da aynı sarmalın (dolayısıyla aynı iç boşluğun) içinde kalmalı.
 *
 * `baslik` da gövdeden AYRI döner: referans panelde `.insp-sec-t` (ref:518),
 * sekmede `.sect-t` (ref:611) kullanıyor — iki farklı ölçü, iki farklı
 * çizgi konumu. Başlığı çağıran bassın ki her yerleşim kendi sınıfını koysun.
 */
export function useAnalizFormu({
  tip,
  siraNo,
  duzenlenen,
  bekliyor,
  yazabilir,
  kaynakAd,
  esikler,
  onKaydet,
}: FormAyari) {
  const [deger, setDeger] = useState<Record<string, string>>({})
  const [gerekce, setGerekce] = useState('')
  const [uyari, setUyari] = useState<string | null>(null)

  // Revizyona girilince alanlar düzenlenen kaydın değerleriyle dolar.
  useEffect(() => {
    if (!duzenlenen) {
      setDeger({})
      setGerekce('')
      return
    }
    const d: Record<string, string> = {}
    for (const a of ALANLAR[tip]) {
      const v = duzenlenen[a.anahtar as keyof AnalizDto] as number | null
      if (v !== null && v !== undefined) d[a.anahtar] = trGiris(v)
    }
    setDeger(d)
    setGerekce('')
  }, [duzenlenen, tip])

  const sayi = (a: keyof AnalizAlanlari): number | null => {
    const v = deger[a]
    return v === undefined || v.trim() === '' ? null : sayiyaCevir(v)
  }

  const alanlar = useMemo(() => {
    const o: AnalizAlanlari = {}
    for (const a of ALANLAR[tip]) o[a.anahtar] = sayi(a.anahtar)
    return o
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deger, tip])

  const sapma = kulSapmasi(alanlar.okm ?? null, alanlar.kul ?? null)
  const kulTamam = kulGecerliMi(alanlar.okm ?? null, alanlar.kul ?? null)

  function kaydet() {
    const eksik = ZORUNLU[tip].filter((z) => alanlar[z] === null || alanlar[z] === undefined)
    if (eksik.length > 0) {
      setUyari(ZORUNLU_MESAJ[tip])
      return
    }
    if (tip === 'lot' && !kulTamam) {
      setUyari(`OKM + Kül ≈ 100 kontrolü sağlanmadı (tolerans ±${KUL_TOLERANS})`)
      return
    }
    if (duzenlenen && gerekce.trim() === '') {
      setUyari('K15: düzeltme revizyondur — gerekçe zorunlu')
      return
    }
    setUyari(null)
    onKaydet(alanlar, duzenlenen ? gerekce.trim() : undefined)
  }

  // Referans panelde fermenter/ürün başlığına kaynağın adını ekliyor
  // (ref:532 · 549), lot dalında (ref:518) eklemiyor. Sekme üç tipte de eksiz.
  const baslikEki = !duzenlenen && kaynakAd && tip !== 'lot' ? ` — ${kaynakAd}` : ''

  const baslik = duzenlenen
    ? `Analizi Düzenle · #${duzenlenen.siraNo}`
    : `Yeni Analiz · #${siraNo}${baslikEki}`

  const butonMetni = bekliyor
    ? 'Kaydediliyor…'
    : duzenlenen
      ? 'Güncelle'
      : `Analizi kaydet · #${siraNo}`

  // Fermenterde dolu her ölçüm kendi rozetini basar (ref:763-769); hiçbiri
  // dolmamışken referansın yer tutucu metni durur.
  const canliRozetler = CANLI_ROZET.flatMap((r) => {
    const deger = alanlar[r.anahtar] ?? null
    if (deger === null) return []
    const b = bantBul(esikler, r.gosterge, deger)
    return [{ ...r, bant: b }]
  })

  const govde = (
    <>
      <div className="fgrid">
        {ALANLAR[tip].map((a) => (
          <div className="fld" key={a.anahtar}>
            <label>{a.etiket}</label>
            <input
              className="in mono"
              placeholder={a.yerTutucu}
              value={deger[a.anahtar] ?? ''}
              disabled={!yazabilir}
              onChange={(e) => setDeger((d) => ({ ...d, [a.anahtar]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      {/* LOT: OKM + Kül = 100 ± 3 canlı rozeti (butonu KİLİTLEMEZ) */}
      {tip === 'lot' && (
        <div style={{ marginTop: 6 }}>
          <span
            className={
              sapma === null
                ? 'badge b-gray'
                : kulTamam
                  ? 'badge b-navy'
                  : 'badge b-amber'
            }
          >
            {sapma === null
              ? 'OKM + Kül kontrolü bekleniyor'
              : kulTamam
                ? `OKM + Kül = ${tr((alanlar.okm ?? 0) + (alanlar.kul ?? 0), 1)} ✓`
                : `OKM + Kül = ${tr((alanlar.okm ?? 0) + (alanlar.kul ?? 0), 1)} — tolerans ±${KUL_TOLERANS} dışı`}
          </span>
        </div>
      )}

      {/* FERMENTER: canlı eşik rozetleri (ref:540 · 763-769) — butonu KİLİTLEMEZ */}
      {tip === 'fermenter' && (
        <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {canliRozetler.length === 0 ? (
            <span className="cell-sub">Değer girdikçe eşik rozetleri belirir</span>
          ) : (
            canliRozetler.map((r) => (
              <span className={BANT_SINIFI[r.bant]} key={r.anahtar}>
                {r.etiket} {BANT_ETIKET[r.bant]}
              </span>
            ))
          )}
        </div>
      )}

      {/* Revizyon gerekçesi (K15) */}
      {duzenlenen && (
        <div className="fld" style={{ marginTop: 8 }}>
          <label>Revizyon gerekçesi (zorunlu)</label>
          <input
            className="in"
            placeholder="Örn. kalibrasyon sonrası yeniden ölçüm"
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
          />
          <div className="fld-hint">
            Analiz silinmez — kayıt güncellenir ve revizyon sayacı artar (K15).
          </div>
        </div>
      )}

      {uyari && (
        <div className="fld-hint" style={{ marginTop: 6, color: 'var(--red)' }}>
          {uyari}
        </div>
      )}
    </>
  )

  return { govde, kaydet, butonMetni, baslik }
}

/**
 * Sekme yerleşimi (ref:611-615): kart başlığı `.sect-t` (çizgi başlığın
 * ALTINDA), alanlar ve butonlar ortak bir iç boşluk kutusunda. Panel
 * yerleşimi için bu bileşen değil, doğrudan {@link useAnalizFormu} kullanılır —
 * orada başlık `.insp-sec-t`, buton `.insp-f` altlığına gider.
 *
 * Kartı ve altındaki `.pnote` dipnotunu çağıran koyar (ref:611/616).
 */
export default function AnalizFormu({
  onVazgec,
  ...ayar
}: FormAyari & { onVazgec?: () => void }) {
  const { govde, kaydet, butonMetni, baslik } = useAnalizFormu(ayar)

  return (
    <>
      <div className="sect-t">{baslik}</div>

      <div style={{ padding: '9px 12px 11px' }}>
        {govde}

        <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
          {ayar.duzenlenen && onVazgec && (
            <button className="btn btn-ghost btn-sm" type="button" onClick={onVazgec}>
              Vazgeç
            </button>
          )}
          <button
            className="btn btn-pri btn-sm"
            type="button"
            style={{ flex: 1, justifyContent: 'center' }}
            disabled={!ayar.yazabilir || ayar.bekliyor}
            title={ayar.yazabilir ? undefined : 'Laboratuvar modülünde yazma yetkiniz yok'}
            onClick={kaydet}
          >
            {butonMetni}
          </button>
        </div>
      </div>
    </>
  )
}
